import { QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import i18n from 'configs/i18n';
import { t } from 'i18next';

import { TIMEOUT_ERROR_MESSAGE } from 'api/base';
import { ERROR_MESSAGES, STATUSES } from 'constants/common';
import { handleRequestError } from 'routes/ErrorBoundary';
import { throttle } from 'utils/common';
import { captureException } from 'utils/sentryLazy';
import { IRequestError, QueryError } from 'utils/types';

const MAX_RETRY_COUNT = 3;

const throttledCaptureMutationException = throttle((error) => {
  captureException(error);
}, 1000);

const throttledCaptureQueryException = throttle((error) => {
  captureException(error);
}, 1000);

export const isPasswordExpiredError = (errorStatus?: number, errorMessage?: string) => {
  return (
    errorStatus === STATUSES.AUTHORIZATION &&
    errorMessage === ERROR_MESSAGES.UPDATE_PASSWORD
  );
};

export const defaultQueryRetry = (failureCount: number, error: Error) => {
  const isMaxRetry = failureCount === MAX_RETRY_COUNT;
  const isTimeoutError = (error as QueryError)?.message === TIMEOUT_ERROR_MESSAGE;
  const isAuthorizationError =
    (error as IRequestError)?.response?.status === STATUSES.AUTHENTICATION;

  return !isAuthorizationError && !isMaxRetry && !isTimeoutError;
};

export const defaultMutationThrowOnError = (err: Error) => {
  const error = err as AxiosError<{
    data?: { message?: string };
    errors: Record<string, string>;
    fieldMessages: Record<string, string>;
    message?: string;
  }>;

  const errorStatus = error.response?.status;
  const errorData = error.response?.data;

  const errorsList =
    error.response?.data?.errors || error.response?.data?.fieldMessages || {};

  const isAuth = errorStatus === STATUSES.AUTHENTICATION;
  const isConflict = errorStatus === STATUSES.CONFLICT;
  const isInvalidData = errorStatus === STATUSES.INVALID_DATA;
  const isOtpError = errorStatus === STATUSES.DATA_ERROR;

  let throwOnError = true;

  if (
    isAuth ||
    isConflict ||
    isInvalidData ||
    isOtpError ||
    isPasswordExpiredError(errorStatus, errorData?.message)
  ) {
    throwOnError = false;
  } else {
    throwOnError = !Object.keys(errorsList).length;
  }

  if (!throwOnError) {
    // React ErrorBoundary component already captures errors
    throttledCaptureMutationException(error);
  }

  return throwOnError;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      retry: 0,
      throwOnError: defaultMutationThrowOnError,
    },
    queries: {
      gcTime: 20 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: defaultQueryRetry,
      staleTime: 1 * 1000 * 60,
      throwOnError: (error) => {
        let throwOnError = true;

        if ((error as QueryError)?.message === TIMEOUT_ERROR_MESSAGE) {
          // This needs for GET queries, otherwise ErrorBoundary not working
          throwOnError = false;
        } else if (
          (error as AxiosError)?.config?.url?.includes('auth/token/refresh') &&
          (error as AxiosError)?.response?.status === STATUSES.INVALID_DATA
        ) {
          throwOnError = false;
        } else {
          throwOnError =
            (error as IRequestError)?.response?.status !== STATUSES.AUTHENTICATION;
        }

        if (!throwOnError) {
          // React ErrorBoundary component already captures errors
          throttledCaptureQueryException(error);
        }

        return throwOnError;
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      throttledCaptureQueryException(error);
      handleRequestError({ error: error as QueryError, i18n, t });
    },
  }),
});
